import type { LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useId } from 'react';

import { cn } from '../../lib/cn.js';
import { TRANSITION } from '../motion.js';

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon: LucideIcon;
}

/**
 * Controle segmentado quadrado, para trocar o modo de uma tela (design.md §8).
 *
 * Mesma casca da barra de filtros em que mora: 40px, fundo afundado e cantos
 * retos. A opção escolhida é a superfície clara com o ícone em `primary`, e a
 * superfície **desliza** até a nova escolha, como na aba (`layoutId`). É um
 * grupo de botões de alternância (`aria-pressed`), não uma lista de abas: a
 * escolha muda como o mesmo conteúdo aparece, não qual conteúdo.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** Nome do grupo para leitor de tela: o que está sendo escolhido. */
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  const id = useId();

  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex h-10 items-center gap-1 bg-sunken p-1"
    >
      {options.map((option) => {
        const active = option.value === value;
        const Icon = option.icon;

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'relative flex h-8 items-center gap-1.5 px-3 text-[13px] whitespace-nowrap transition-colors duration-100',
              active ? 'font-medium text-ink' : 'text-ink-3 hover:text-ink',
            )}
          >
            {active ? (
              <motion.span
                layoutId={`${id}-ativa`}
                transition={TRANSITION.layout}
                className="absolute inset-0 bg-surface"
                aria-hidden
              />
            ) : null}
            <Icon
              size={15}
              strokeWidth={1.5}
              className={cn('relative z-10 shrink-0', active && 'text-primary')}
              aria-hidden
            />
            <span className="relative z-10">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
